import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import NewsAlerts from '../NewsAlerts/NewsAlertsDrawer';
import ChannelList from '../ChannelList/ChannelList';

import { ReactComponent as RBNLogo } from '../images/rbn.svg'
import { ReactComponent as ChannelsIcon } from '../images/channels.svg'
import { ReactComponent as EventsIcon } from '../images/events.svg'
import { ReactComponent as SettingsIcon } from '../images/settings.svg'
import { IoMdHelpCircleOutline } from 'react-icons/io';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import { IconType } from 'react-icons';
import { doSwitchDrawer } from '../store/actions/location';
import { DrawerTypes } from '../store/reducers/drawer';
import { selectDrawer, selectMobileMenuOpen } from '../store/selectors'
import FormPreferences from '../Forms/Preferences';
import FormHelp from '../Forms/Help';

import styles from './DrawerController.module.scss'
import { selectAlertsCount } from '../store/selectors/alerts';
import { doSetMobileMenuOpen } from '../store/actions/system';


type DrawerConfigType = {
  menuName: string,
  title: string,
  component: any,
  icon: IconType | React.FunctionComponent<React.SVGProps<SVGSVGElement>>,
  iconHeight: number,
  badgeCount?: number
}

export default function DrawerController() {
  const currentDrawer = useSelector(selectDrawer);
  const alertsCount = useSelector(selectAlertsCount);
  const mobileMenuOpen = useSelector(selectMobileMenuOpen);

  const dispatch = useDispatch();

  let drawerConfig: {[key: string]: DrawerConfigType} = {
    channels: {
      menuName: "Channels",
      title: "Channels",
      component: ChannelList,
      icon: ChannelsIcon,
      iconHeight: 38,
    },
    events: {
      menuName: "Headliners",
      title: "Today's Headliners",
      component: NewsAlerts,
      icon: EventsIcon,
      iconHeight: 26,
      badgeCount: alertsCount,
    },
    preferences: {
      menuName: "Preferences",
      title: "Preferences",
      component: FormPreferences,
      icon: SettingsIcon,
      iconHeight: 35,
    },
    help: {
      menuName: "Help",
      title: "Help",
      component: FormHelp,
      icon: IoMdHelpCircleOutline,
      iconHeight: 35
    }
  }

  if (process.env.REACT_APP_MODE === 'development') {
    const {MdDeveloperBoard} = require('react-icons/md');
    const Devel = require('../Devel/Devel').default;

    drawerConfig.devel = {
      menuName: "Devel",
      title: "Development",
      component: Devel,
      icon: MdDeveloperBoard,
      iconHeight: 35,
    }
  }

  let drawersTabs = [];

  for (let [drawer, config] of Object.entries(drawerConfig)) {
    drawersTabs.push(
      <DrawerTab
        key={drawer}
        icon={config.icon}
        iconHeight={config.iconHeight}
        name={config.menuName}
        isActive={drawer===currentDrawer}
        badgeCount={config.badgeCount??0}
        openDrawer={() => dispatch(doSwitchDrawer(drawer as DrawerTypes))}
      />
    )
  }

  const drawerOpen = currentDrawer !== 'none';

  return (
    <div className={styles.wrapper}>
      <div className={styles.tabs}>
        <RBNLogo className={styles.logo} />
        <div className={styles.tabsInner}>
          { drawersTabs }
        </div>
      </div>
      {drawerOpen &&
        <Drawer
          component={drawerConfig[currentDrawer]?.component}
          title={drawerConfig[currentDrawer]?.title}
          handleClose={() => mobileMenuOpen ? dispatch(doSetMobileMenuOpen(false)) : dispatch(doSwitchDrawer(DrawerTypes.none))}
        />
      }
    </div>
  )
}

type DrawerProps = {
  component: any,
  title: string
  handleClose: (e: React.MouseEvent) => void,
}

const Drawer = function(props: DrawerProps) {
  const Component = props.component;

  return (
    <div className={styles.drawer}>
      <div className={styles.drawerTitle}>
        { props.title }
        <IoMdCloseCircleOutline onClick={props.handleClose} className={styles.drawerClose} size="22px" />
      </div>
      { Component && <div className={styles.drawerComponentWrapper}><Component /></div> }
    </div>
  )
}

// Drawer Tab
type DrawerTabProps = {
  name: string,
  icon: IconType | React.FunctionComponent<React.SVGProps<SVGSVGElement>>,
  iconHeight: number,
  isActive: boolean,
  badgeCount: number,
  openDrawer: (e: React.MouseEvent) => void
}

const DrawerTab = function(props: DrawerTabProps) {
  const Icon = props.icon;

  return (
    <button className={props.isActive ? styles.activeTab : styles.tab} onClick={props.openDrawer}>
      { props.badgeCount > 0 && <div className={styles.tabBadge}>{ props.badgeCount }</div>}
      <Icon style={{height: props.iconHeight + "px"}} /> { props.name }
    </button>
  )
}
